import React, { FC, useEffect } from 'react'
import { useConfirmStep } from '@ally/create-flow'
import styled from 'styled-components'
import {
  Transfer,
  FromAccount,
  ToAccount,
  Amount,
  DeliveryDate,
  getConfirmationMessage,
  TransferSpeed,
  Fee,
  ConfirmationNumber,
} from '@ally/transfers-toolkit'
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Space,
  Stack,
  useMediaSize,
} from '@ally/metronome-ui'

import { muiPrinterOutline } from '@ally/metronome-icons'
import { useHostServices } from '../../../HostServices'

const BtnInvisibleForPrint = styled(Button)`
  @media print {
    visibility: hidden;
  }
`

const Confirm: FC = () => {
  const { item: transfer, repeat, focusRef } = useConfirmStep<Transfer>()
  const { log } = useHostServices()

  const {
    fromAccount,
    toAccount,
    amount,
    transferSpeed,
    confirmationNumber,
    warning,
  } = transfer

  const isLgUp = useMediaSize('LgUp')

  useEffect(() => {
    ;(window as any).allytm.event('customEvent', {
      name: 'TRANSFER_WIDGET_SUCCESS',
    })
    if (warning) {
      log.track(`TRANSFER_WIDGET_SUCCESS-${warning}`)
    } else {
      log.track('TRANSFER_WIDGET_SUCCESS')
    }
  }, [confirmationNumber, warning])

  return (
    <>
      {warning && (
        <Alert
          disableContainer
          variant="warning"
          heading={warning}
          focusOnMount
        />
      )}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        pb="sm"
      >
        <Alert
          disableContainer
          variant="success"
          heading={getConfirmationMessage(transfer)}
          ref={warning ? undefined : focusRef}
        />
        {isLgUp && (
          // TODO: Should be XL
          <BtnInvisibleForPrint
            aria-label="print page"
            aria-hidden
            variant="link"
            onClick={window.print}
            icon={{ map: muiPrinterOutline }}
          />
        )}
      </Box>

      {/* TODO: fix Stack component and move this inside of Stack */}
      {!!confirmationNumber && (
        <Box mb="xs">
          <ConfirmationNumber.Display number={confirmationNumber} />
        </Box>
      )}

      <Stack space="xs">
        <Box>
          <FromAccount.Display account={fromAccount} />
        </Box>

        <Box>
          <ToAccount.Display account={toAccount} />
        </Box>

        <Box>
          <Amount.Display amount={amount} />
        </Box>

        <Box>
          <DeliveryDate.Display transfer={transfer} />
        </Box>

        <Box>
          <TransferSpeed.Display
            transferSpeed={transferSpeed}
            fromAccount={fromAccount}
            toAccount={toAccount}
          />
        </Box>

        <Box>
          <Fee.Display fee={0} />
        </Box>
      </Stack>

      <Space mt="sm">
        {isLgUp ? (
          <Button
            variant="link"
            text="Make another one-time transfer"
            onClick={repeat}
          />
        ) : (
          // We need the formatting from ButtonGroup on less than large
          // breakpoints. Since it requires 2+ children, null is used as the
          // second child.
          <ButtonGroup>
            <Button
              variant="link"
              text="Make another one-time transfer"
              onClick={repeat}
            />
            {null}
          </ButtonGroup>
        )}
      </Space>
    </>
  )
}

export default Confirm
